/**
 * useLocations Hook
 * 
 * React Query hooks for fetching and managing locations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@/core';
import { notify } from '@/shared/services';
import { LocationService } from '../services/locationService';
import type {
  Location,
  LocationFilters,
  LocationInput,
  LocationUpdateInput,
  LocationSummary,
} from '../types';

// Query keys for cache management
export const locationKeys = {
  all: ['locations'] as const,
  lists: () => [...locationKeys.all, 'list'] as const,
  list: (filters?: LocationFilters) => [...locationKeys.lists(), filters] as const,
  summaries: (filters?: LocationFilters) => [...locationKeys.all, 'summaries', filters] as const,
  details: () => [...locationKeys.all, 'detail'] as const,
  detail: (id: string) => [...locationKeys.details(), id] as const,
  bySlug: (slug: string) => [...locationKeys.all, 'slug', slug] as const,
  withBilling: (id: string) => [...locationKeys.detail(id), 'billing'] as const,
};

/**
 * Hook for fetching all locations with optional filters
 */
export function useLocations(filters?: LocationFilters) {
  return useQuery({
    queryKey: locationKeys.list(filters),
    queryFn: () => LocationService.getAll(filters),
  });
}

/**
 * Hook for fetching location summaries (lightweight)
 */
export function useLocationSummaries(filters?: LocationFilters) {
  return useQuery({
    queryKey: locationKeys.summaries(filters),
    queryFn: () => LocationService.getSummaries(filters),
  });
}

/**
 * Hook for fetching a single location by ID
 */
export function useLocation(id: string | undefined) {
  return useQuery({
    queryKey: locationKeys.detail(id!),
    queryFn: () => LocationService.getById(id!),
    enabled: !!id,
  });
}

/**
 * Hook for fetching a single location by slug
 */
export function useLocationBySlug(slug: string | undefined) {
  return useQuery({
    queryKey: locationKeys.bySlug(slug!),
    queryFn: () => LocationService.getBySlug(slug!),
    enabled: !!slug,
  });
}

/**
 * Hook for fetching location with billing profiles
 */
export function useLocationWithBilling(id: string | undefined) {
  return useQuery({
    queryKey: locationKeys.withBilling(id!),
    queryFn: () => LocationService.getWithBillingProfiles(id!),
    enabled: !!id,
  });
}

/**
 * Hook for creating a new location
 */
export function useCreateLocation() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (input: LocationInput) => LocationService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: locationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: locationKeys.all });
    },
    onError: () => notify.error(t('locations.messages.createError', 'Failed to create location')),
  });
}

/**
 * Hook for updating an existing location
 */
export function useUpdateLocation() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (input: LocationUpdateInput) => LocationService.update(input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: locationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: locationKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: locationKeys.bySlug(data.slug) });
    },
    onError: () => notify.error(t('locations.messages.updateError', 'Failed to update location')),
  });
}

/**
 * Hook for deleting a location
 */
export function useDeleteLocation() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (id: string) => LocationService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: locationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: locationKeys.all });
    },
    onError: () => notify.error(t('locations.messages.deleteError', 'Failed to delete location')),
  });
}

/**
 * Hook for updating location status
 */
export function useUpdateLocationStatus() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: Location['status'] }) =>
      LocationService.updateStatus(id, status),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: locationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: locationKeys.detail(data.id) });
    },
    onError: () => notify.error(t('locations.messages.statusError', 'Failed to update location status')),
  });
}

/**
 * Hook for checking slug availability
 */
export function useSlugAvailability(slug: string, excludeId?: string) {
  return useQuery({
    queryKey: ['location-slug', slug, excludeId],
    queryFn: () => LocationService.isSlugAvailable(slug, excludeId),
    enabled: slug.length > 2,
  });
}
